import React, { useState } from "react";
import { BasicStep, Step } from "../../types";
import { ToastMessage } from "@allaria-tech/matter/ToastMessage";
import SubStepper from "../SubStepper/SubStepper";
import useWindowWidth from "../../hooks/useWindowWidth";
import useMobileLayout from "../../hooks/useMobileLayout";
import DotStep from "../DotStep/DotStep";
import { Typography } from "@allaria-tech/matter/Typography";
import styles from "./FormContainer.module.scss";

interface FormContainerProps {
  currentStep?: Step;
  children: React.ReactNode;
  currentSubStep: number;
  className?: string;
  classNameFormContainer?: string;
  hideFormHeader?: boolean;
}

const FormContainer = ({
  currentStep,
  currentSubStep,
  children,
  className,
  classNameFormContainer,
  hideFormHeader,
}: FormContainerProps) => {
  const {
    formContainer,
    header,
    headerContainer,
    childrenStyle,
    overflowWrapper,
    overflowScrollGradient,
    cryptoFullScreenHeader,
    titleRow,
    cryptoFullScreenFormHeader,
  } = styles;

  const hasSubSteps = !!currentStep?.subSteps;
  const subStep = currentStep?.subSteps?.find(
    (s) => s.step === currentSubStep,
  ) as BasicStep;
  const hideSubStepper = subStep?.hideStepper;

  const subtitle = currentStep?.subtitle || subStep?.subtitle;

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const mobileTitle = subStep?.mobileName || currentStep?.mobileName;

  const title =
    (isMobile && mobileTitle) || subStep?.titleOfStep || currentStep?.name;
  const titleColor = subStep?.titleColor ?? currentStep?.titleColor;
  const dotStep = subStep?.dotStep ?? currentStep?.dotStep;

  const { isCryptoFullScreen } = useMobileLayout();
  const hasFields = !!subStep?.fields?.length;

  const [showOverflowGradient, setShowOverflowGradient] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const node = event.target as HTMLInputElement;
    const reachedTop = node.scrollTop === 0;

    if (reachedTop) setShowOverflowGradient(false);
    else if (!showOverflowGradient) setShowOverflowGradient(true);
  };

  return (
    <div className={`${formContainer} ${classNameFormContainer}`}>
      <ToastMessage
        messageId="formAlert"
        position="TOP"
        className="toastMessage"
      />
      {!hideFormHeader && (
        <div
          className={`${headerContainer} ${isCryptoFullScreen ? (hasFields ? cryptoFullScreenFormHeader : cryptoFullScreenHeader) : ""}`}
        >
          <div className={header}>
            <div className={titleRow}>
              <Typography
                type="HEADING"
                size={isCryptoFullScreen ? "M" : "L"}
                weight="BASE"
                color={isCryptoFullScreen ? "var(--neutrals-600)" : titleColor}
              >
                {title}
              </Typography>
              {dotStep && (
                <DotStep current={dotStep.current} total={dotStep.total} />
              )}
            </div>
            {!hideSubStepper && hasSubSteps && (
              <SubStepper
                key={currentStep?.step}
                currentSubStep={currentSubStep}
                subSteps={currentStep?.subSteps}
              />
            )}
            {subtitle && <Typography type="BODY">{subtitle}</Typography>}
          </div>
        </div>
      )}
      {!isMobile ? (
        <div
          className={`${overflowWrapper} ${
            showOverflowGradient ? overflowScrollGradient : ""
          }`}
        >
          <div
            className={`${childrenStyle} scrollbar ${className} ${classNameFormContainer}`}
            onScroll={handleScroll}
          >
            {children}
          </div>
        </div>
      ) : (
        <div
          className={`${childrenStyle} scrollbar ${className} ${classNameFormContainer}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default FormContainer;
